// API conventions shared by every module (architecture/architecture.md Section 3.11).
import { z } from 'zod';

/** Stable error codes in problem+json bodies (RFC 9457 + `code`). */
export const ERROR_CODES = [
  'validation_failed',
  'unauthenticated',
  'forbidden',
  /** 403 on POST /mcp: a browser Origin that isn't the tool's own or listed (poc CT-010). */
  'forbidden_origin',
  'not_found',
  /** 410: the thing existed but was removed (an attachment tombstone, REQ-19.1). */
  'gone',
  'revision_conflict',
  'move_not_allowed',
  'reason_required',
  'rate_limited',
  'payload_too_large',
  'app_switched_off',
  'unknown_app_key',
  'address_not_registered',
  'not_visible',
  'conflict',
  'unavailable',
  'internal',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 50;

/** `page` from 1, `pageSize` default 50, at most 100. */
export const PageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type PageQuery = z.infer<typeof PageQuerySchema>;

/** Success body: `{ "data": ... }`. */
export function envelope<T extends z.ZodType>(schema: T) {
  return z.object({ data: schema });
}

/** List body: `{ "data": { "items": [...], "page": 1, "pageSize": 50, "total": 123 } }`. */
export function pagedEnvelope<T extends z.ZodType>(item: T) {
  return z.object({
    data: z.object({
      items: z.array(item),
      page: z.number().int().min(1),
      pageSize: z.number().int().min(1).max(MAX_PAGE_SIZE),
      total: z.number().int().min(0),
    }),
  });
}

export const FieldErrorSchema = z.object({
  path: z.string(),
  message: z.string(),
});
export type FieldError = z.infer<typeof FieldErrorSchema>;

/** application/problem+json (RFC 9457) plus `code`, optional `errors[]`, and code-specific fields. */
export const ProblemSchema = z
  .object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string(),
    code: ErrorCodeSchema,
    instance: z.string().optional(),
    requestId: z.string().optional(),
    errors: z.array(FieldErrorSchema).optional(),
  })
  .catchall(z.unknown());
export type Problem = z.infer<typeof ProblemSchema>;

export const PROBLEM_CONTENT_TYPE = 'application/problem+json';

export const HealthStatusSchema = z.object({
  status: z.enum(['ok', 'unavailable']),
  checks: z.record(z.string(), z.enum(['ok', 'failed'])).optional(),
});
export type HealthStatus = z.infer<typeof HealthStatusSchema>;
